import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getCalculatorHelpModalOpen, toggleCalculatorHelpModal } from '../../admin/dataset-builder/store';
import { ConfirmationModal } from '../modal/ConfirmationModal';
import styles from './Calculator.module.scss';
import { ModalHeader } from '../modal/ModalHeader';
import { Calculator } from '../icons';
import { Scrollable } from '../scrollable/Scrollable';
import { CalculatorPadOptionDB, getAllPadOptions } from '../../admin/calculator/store';

const generalExplanation = [
    'Legal prose is, by its nature, incredibly flexible.  However, there are distinct disadvantages associated with this degree of flexibility.  Specifically, the same underlying concept within a document can be expressed in a wide variety of ways.  This does not assist in the process of analysing and comparing information and risks across a portfolio of ostensibly similar documents.',
    'We have tried to provide an approach which is sufficiently flexible to enable users to capture the essence of the underlying concepts within a document, but which imposes a degree of uniformity so as to enable comparison and risk assessment.',
    'Using the "Calculator Keypads" we have provided, users can select strings of "tokens" which replicate the legal prose found within actual agreements.  We hope that the process is quite intuitive, but have provided some explanations of some of the "tokens" which exist so as to assist general understanding.'
];

export const CalculatorHelpModal: React.FC = () => {
    const [selectedHelp, setSelectedHelp] = useState<CalculatorPadOptionDB | null>(null);
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector(getCalculatorHelpModalOpen);
    const allPadOptions = useAppSelector(getAllPadOptions);
    const closeModal = () => dispatch(toggleCalculatorHelpModal());

    const padOptionsWithDescriptions = useMemo(() => allPadOptions.filter(({ description }) => description.length > 0), [allPadOptions]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            closeLabel='Close'
            showConfirm={false}
        >
            <div className={styles.calculatorHelpWrapper}>
                <ModalHeader icon={Calculator} label='Calculator Help' />
                <div className={styles.helpContent}>
                    <div className={styles.generalHelpContent}>
                        <Scrollable>
                            {generalExplanation.map((help, index) => (
                                <div key={index} className={styles.generalHelp}>{help}</div>
                            ))}
                        </Scrollable>
                    </div>
                    <div className={styles.inputHelpContent}>
                        <div className={styles.inputHelpTitle}>Click one of the following calculator inputs for more information about how it should be used</div>
                        <div className={styles.inputHelpWrapper}>
                            <div className={styles.selectedHelpWrapper}>
                                {!isNull(selectedHelp) &&
                                    <div className={styles.selectedHelp}>
                                        <div className={styles.selectedHelpButton} style={{ fontSize: selectedHelp.label.length <= 2 ? '30px' : '20px' }}>{selectedHelp.label}</div>
                                        <div className={styles.selectedHelpExplanation}>{selectedHelp.description}</div>
                                    </div>}
                            </div>
                            <div className={styles.helpKeypad}>
                                <Scrollable>
                                    <div className={styles.inputHelpItems}>
                                        {padOptionsWithDescriptions.map(input => (
                                            <button key={input.label} className={classnames(styles.inputHelp, { [styles.selectedInput]: input === selectedHelp })} onClick={() => setSelectedHelp(input)}>
                                                <div className={styles.inputHelpLabel} style={{ fontSize: input.label.length <= 2 ? '20px' : '16px' }}>{input.label}</div>
                                            </button>
                                        ))}
                                    </div>
                                </Scrollable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ ConfirmationModal>
    );
};
